import type { ListBuilderConfiguration } from '../types';
import {
	createClientNameColumn,
	createContactInfoColumn,
	createLocationColumn,
} from './client/columns';
import { DEFAULT_LIST_REQUIRED_FIELDS } from './client/shared';

/**
 * PD-14077 — Child Accounts tab on the client detail page.
 *
 * A mini version of the main clients list (same shared columns: status dot +
 * linked name + address, type badge + business type + id, phone/email),
 * scoped server-side to clients whose `fk_client__id_parent` is the viewed
 * client (the `_fk_client__id_parent` context param). The actions column
 * exposes "Remove" — which clears the parent link via a confirmation modal
 * in ClientChildAccountsTab; the client itself is never deleted.
 */
export const CLIENT_CHILDREN_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	objectType: 'client',
	lists: [
		{
			id: 'client-children-all',
			cacheTTL: 600000,
			name: 'Child Accounts',
			type: 'default',
			active: true,
			columns: [
				createClientNameColumn(),
				createLocationColumn(),
				createContactInfoColumn(),
				{
					id: 'actions',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'remove-child-action',
									label: 'Remove',
									iconName: 'link-slash',
									callbackName: 'removeChildAccount',
									variant: 'danger',
									visibility: 'visible',
									visibilityCallback: 'shouldShowRemoveButton',
								},
							],
						},
					],
					headers: [{ id: 'h-actions', text: '' }],
					width: 70,
					align: 'center',
					sortable: false,
				},
			],
			description: 'Accounts linked under this client (PD-14077)',
			requiredFields: DEFAULT_LIST_REQUIRED_FIELDS,
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: ['clientSearch'],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: '_id', fieldLabel: 'Internal ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'address1', fieldLabel: 'Address' },
				{ fieldName: 'city', fieldLabel: 'City' },
				{ fieldName: 'state', fieldLabel: 'State' },
				{ fieldName: 'zip', fieldLabel: 'Zip' },
				{ fieldName: 'phone', fieldLabel: 'Phone' },
				{ fieldName: 'email', fieldLabel: 'Email' },
				{ fieldName: 'leadOrCustomer', fieldLabel: 'Lead/Customer' },
				{ fieldName: 'businessType', fieldLabel: 'Business Type' },
			],
		},
	],
	tableSorts: [
		{
			id: 'name-asc',
			name: 'Name (A-Z)',
			fields: [
				{ direction: 'ASC', fieldName: 'client.name', objectTable: 'client' },
			],
			description: 'Sort child accounts by name',
		},
		{
			id: 'name-desc',
			name: 'Name (Z-A)',
			fields: [
				{ direction: 'DESC', fieldName: 'client.name', objectTable: 'client' },
			],
			description: 'Sort child accounts by name descending',
		},
		{
			id: 'type-asc',
			name: 'By Type',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'client.leadOrCustomer',
					objectTable: 'client',
				},
				{ direction: 'ASC', fieldName: 'client.name', objectTable: 'client' },
			],
			description: 'Sort by lead/customer then name',
		},
	],
};
